import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cached = null;

export const fetchVatRate = async () => {
  if (cached !== null) return cached;
  const res = await axios.get(`${API}/settings/vat`);
  cached = parseFloat(res.data.rate) || 0;
  return cached;
};

export const clearVatCache = () => { cached = null; };

export const useVatRate = () => {
  const [rate, setRate] = useState(cached ?? 18);
  useEffect(() => { fetchVatRate().then(setRate).catch(() => {}); }, []);
  return rate;
};

export const vatOf = (subtotal, rate) => (parseFloat(subtotal) || 0) * (parseFloat(rate) || 0) / 100;
export const withVat = (subtotal, rate) => (parseFloat(subtotal) || 0) + vatOf(subtotal, rate);

// Form helper: rate stored on the record (0 = no VAT / legacy record)
export const useRecordVat = () => {
  const currentRate = useVatRate();
  const [recordRate, setRecordRate] = useState(null);
  const [noVat, setNoVat] = useState(false);
  const effectiveRate = noVat ? 0 : (recordRate || currentRate);
  const loadFromRecord = (record) => {
    const r = parseFloat(record?.vatRate) || 0;
    setRecordRate(r || null);
    setNoVat(r === 0);
  };
  return { effectiveRate, noVat, setNoVat, loadFromRecord };
};

const loadExemptNames = async () => {
  const res = await axios.get(`${API}/customers`);
  return new Set((res.data || []).filter((c) => c.vatExempt).map((c) => `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase()));
};

// New forms only: auto-tick "no VAT" when the chosen customer is marked VAT-exempt
export const useExemptCustomerVat = (vatState, customerName, enabled) => {
  const names = useRef(null);
  const autoSet = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const name = (customerName || '').trim().toLowerCase();
    if (!names.current) names.current = loadExemptNames();
    names.current.then((set) => {
      if (set.has(name)) {
        autoSet.current = true;
        vatState.setNoVat(true);
      } else if (autoSet.current) {
        autoSet.current = false;
        vatState.setNoVat(false);
      }
    }).catch(() => { names.current = null; });
  }, [customerName, enabled]);
};
